import {
  type ElementType,
  type ReactNode,
  type HTMLAttributes,
  type LabelHTMLAttributes,
  type JSX,
} from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type TypographyProps } from '@mui/material/Typography';
import {
  type FontWeight,
  getFontWeight,
  getVisuallyHiddenStyles,
} from './typography.utils';
import { useLinkStyles } from './link.styles';
import { useTheme, type ThemeNames } from '../theme';

type BaseTypographyProps<
  T,
  Tag extends ElementType = ElementType,
  Attrs extends object = HTMLAttributes<T>,
> = Attrs & {
  children?: ReactNode;
  fontWeight?: FontWeight;
  color?: string;
  tag?: Tag;
  themeName?: ThemeNames;
  className?: string;
  isVisuallyHidden?: boolean;
};

export type HeadingElementProps = BaseTypographyProps<
  HTMLHeadingElement,
  Exclude<ElementType, 'label'>
> & {
  variant:
    | 'displayL'
    | 'displayM'
    | 'displayS'
    | 'titleH1'
    | 'titleH2'
    | 'titleH3'
    | 'titleH4';
};

type ParagraphElementProps = BaseTypographyProps<
  HTMLParagraphElement,
  Exclude<ElementType, 'label'>
>;

type LabelElementProps = BaseTypographyProps<
  HTMLLabelElement,
  'label',
  LabelHTMLAttributes<HTMLLabelElement>
> & {
  tag: 'label';
};

type SpanElementProps = BaseTypographyProps<
  HTMLSpanElement,
  Exclude<ElementType, 'label'>
>;

type LinkElementProps = BaseTypographyProps<
  HTMLSpanElement,
  Exclude<ElementType, 'label'>
> & {
  startIcon?: ReactNode;
  variant?: 'linkM' | 'linkS' | 'captionLink';
};

const StyledTypography = styled(Typography)<TypographyProps>({});

const LinkElement = ({
  children,
  fontWeight,
  tag = 'span',
  themeName,
  className,
  onClick,
  startIcon,
  color,
  ...props
}: LinkElementProps): JSX.Element => {
  // For link elements, the semantics change depending on the presence of a click handler
  // if there is a click handler, return a button element
  // if there is no click handler, return a span element

  const theme = useTheme(themeName);
  const styles = useLinkStyles({ themeName, color });

  const element = (
    <StyledTypography
      {...props}
      component={tag}
      className={className}
      sx={{
        ...styles.link,
        fontWeight: getFontWeight({ theme, fontWeight }),
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {startIcon}
      {children}
    </StyledTypography>
  );

  if (onClick) {
    return (
      <button
        type='button'
        css={styles.button}
        className={className}
        onClick={onClick}
      >
        {element}
      </button>
    );
  }

  return element;
};

export const Heading = ({
  children,
  fontWeight,
  color,
  variant,
  tag,
  themeName,
  className,
  isVisuallyHidden,
  ...props
}: HeadingElementProps): JSX.Element => {
  const theme = useTheme(themeName);

  const variantToTag: Record<
    HeadingElementProps['variant'],
    'h1' | 'h2' | 'h3' | 'h4'
  > = {
    displayL: 'h1',
    displayM: 'h2',
    displayS: 'h3',
    titleH1: 'h1',
    titleH2: 'h2',
    titleH3: 'h3',
    titleH4: 'h4',
  };

  return (
    <StyledTypography
      {...props}
      variant={variant}
      component={tag || variantToTag[variant]}
      className={className}
      sx={{
        ...(isVisuallyHidden && getVisuallyHiddenStyles()),
        color,
        fontWeight: getFontWeight({ theme, fontWeight }),
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {children}
    </StyledTypography>
  );
};

export const BodyL = ({
  children,
  fontWeight,
  color,
  tag = 'p',
  themeName,
  className,
  isVisuallyHidden,
  ...props
}: ParagraphElementProps | LabelElementProps): JSX.Element => {
  const theme = useTheme(themeName);
  return (
    <StyledTypography
      {...props}
      variant='bodyL'
      component={tag}
      className={className}
      sx={{
        ...(isVisuallyHidden && getVisuallyHiddenStyles()),
        color,
        fontWeight: getFontWeight({ theme, fontWeight }),
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {children}
    </StyledTypography>
  );
};

export const BodyM = ({
  children,
  fontWeight,
  color,
  tag = 'p',
  themeName,
  className,
  isVisuallyHidden,
  ...props
}: ParagraphElementProps | LabelElementProps): JSX.Element => {
  const theme = useTheme(themeName);

  return (
    <StyledTypography
      variant='bodyM'
      component={tag}
      className={className}
      sx={{
        ...(isVisuallyHidden && getVisuallyHiddenStyles()),
        color,
        fontWeight: getFontWeight({ theme, fontWeight }),
        fontFamily: theme.typography.fontFamily,
      }}
      {...props}
    >
      {children}
    </StyledTypography>
  );
};

export const LinkM = (props: LinkElementProps): JSX.Element => {
  return <LinkElement {...props} variant='linkM' />;
};

export const BodyS = ({
  children,
  fontWeight,
  color,
  tag = 'p',
  themeName,
  className,
  isVisuallyHidden,
  ...props
}: ParagraphElementProps | LabelElementProps): JSX.Element => {
  const theme = useTheme(themeName);

  return (
    <StyledTypography
      {...props}
      variant='bodyS'
      component={tag}
      className={className}
      sx={{
        ...(isVisuallyHidden && getVisuallyHiddenStyles()),
        color,
        fontWeight: getFontWeight({ theme, fontWeight }),
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {children}
    </StyledTypography>
  );
};

export const LinkS = (props: LinkElementProps): JSX.Element => {
  return <LinkElement {...props} variant='linkS' />;
};

export const Caption = ({
  children,
  fontWeight,
  color,
  tag = 'span',
  themeName,
  className,
  isVisuallyHidden,
  ...props
}: SpanElementProps): JSX.Element => {
  const theme = useTheme(themeName);
  return (
    <StyledTypography
      {...props}
      variant='caption'
      component={tag}
      className={className}
      sx={{
        ...(isVisuallyHidden && getVisuallyHiddenStyles()),
        color,
        fontWeight: getFontWeight({ theme, fontWeight }),
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {children}
    </StyledTypography>
  );
};

export const CaptionLink = (props: LinkElementProps): JSX.Element => {
  return <LinkElement {...props} variant='captionLink' />;
};
